"use client";

import Link from "next/link";
import { useUser } from "@/hooks/use-async";
import { MainLayout } from "@/components/layout";

export default function Home() {
  const { user } = useUser();

  return (
    <MainLayout>
      {/* Hero Section */}
      <div className="py-20 text-center">
        <h1 className="text-5xl font-bold mb-6">Welcome to Boutika</h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
          The modern e-commerce platform for sellers. Create products, manage
          your inventory, and reach customers worldwide.
        </p>

        <div className="flex gap-4 justify-center">
          {user ? (
            <>
              <Link
                href="/products"
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 text-lg"
              >
                Browse Products
              </Link>
              <Link
                href="/dashboard"
                className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 text-lg"
              >
                My Dashboard
              </Link>
            </>
          ) : (
            <>
              <Link
                href="/products"
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 text-lg"
              >
                Browse Products
              </Link>
              <Link
                href="/register"
                className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 text-lg"
              >
                Start Selling
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16">
        <h2 className="text-3xl font-bold text-center mb-12">
          Why Choose Boutika?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="nc-box-has-hover p-6">
            <h3 className="text-xl font-semibold mb-3">
              Easy Product Management
            </h3>
            <p className="text-gray-600">
              Create, update, and manage your products with ease. Add colors,
              sizes, and photos to showcase your items.
            </p>
          </div>

          <div className="nc-box-has-hover p-6">
            <h3 className="text-xl font-semibold mb-3">
              Flexible Subscriptions
            </h3>
            <p className="text-gray-600">
              Choose a plan that fits your business needs. Start free with 5
              products or upgrade for unlimited listings.
            </p>
          </div>

          <div className="nc-box-has-hover p-6">
            <h3 className="text-xl font-semibold mb-3">Customer Reviews</h3>
            <p className="text-gray-600">
              Build trust with customer ratings and reviews. Receive feedback
              and improve your products.
            </p>
          </div>

          <div className="nc-box-has-hover p-6">
            <h3 className="text-xl font-semibold mb-3">Wishlist Feature</h3>
            <p className="text-gray-600">
              Customers can save their favorite products. Create a personalized
              shopping experience.
            </p>
          </div>

          <div className="nc-box-has-hover p-6">
            <h3 className="text-xl font-semibold mb-3">Secure Transactions</h3>
            <p className="text-gray-600">
              Built on secure infrastructure. All user data is encrypted and
              protected with JWT authentication.
            </p>
          </div>

          <div className="nc-box-has-hover p-6">
            <h3 className="text-xl font-semibold mb-3">24/7 Support</h3>
            <p className="text-gray-600">
              Our dedicated support team is here to help you succeed. Get
              answers to your questions anytime.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 bg-gray-100 rounded-lg px-8">
        <h2 className="text-3xl font-bold text-center mb-12">Simple Pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { name: "Free", price: "0", products: "5" },
            { name: "Pro", price: "9.99", products: "50" },
            { name: "Business", price: "49.99", products: "500" },
            { name: "Enterprise", price: "99.99", products: "∞" },
          ].map((plan) => (
            <div key={plan.name} className="nc-box-has-hover p-6 text-center">
              <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
              <p className="text-3xl font-bold text-green-600 mb-2">
                ${plan.price}
                <span className="text-sm text-gray-600">/month</span>
              </p>
              <p className="text-gray-600 mb-6">
                Up to {plan.products} products
              </p>
              <Link
                href="/subscriptions"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 inline-block"
              >
                Learn More
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 text-center">
        <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
        <p className="text-xl text-gray-600 mb-8">
          Join thousands of sellers on Boutika today
        </p>
        {!user && (
          <Link
            href="/register"
            className="bg-green-600 text-white px-8 py-3 rounded-lg hover:bg-green-700 text-lg inline-block"
          >
            Create Your Account
          </Link>
        )}
      </div>
    </MainLayout>
  );
}
